<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import ArrowIcon from '@/components/shared/ArrowIcon.vue'
import participantRegistrationWizardImage from '@/assets/marketing/participant-registration-wizard.png'
import participantDashboardImage from '@/assets/marketing/participant-dashboard.png'

const { t } = useI18n()
</script>

<template>
  <section class="bg-slate-50 py-24">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">

      <!-- Spotlight 1: Registration wizard (image left, text right) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div class="rounded-xl overflow-hidden shadow-2xl max-w-2xl mx-auto">
          <img :src="participantRegistrationWizardImage" alt="Participant Registration Wizard" class="border border-slate-200 shadow-xl w-full" />
        </div>
        <div>
          <p class="text-sky-600 font-semibold text-sm uppercase tracking-widest">
            {{ t('participants_lp.spotlights.wizard_label') }}
          </p>
          <h2 class="text-3xl md:text-4xl font-bold text-slate-900 mt-3">
            {{ t('participants_lp.spotlights.wizard_heading') }}
          </h2>
          <p class="text-slate-500 mt-4 leading-relaxed">
            {{ t('participants_lp.spotlights.wizard_body') }}
          </p>
          <ul class="mt-6 space-y-2">
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('participants_lp.spotlights.wizard_feature1') }}
            </li>
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('participants_lp.spotlights.wizard_feature2') }}
            </li>
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('participants_lp.spotlights.wizard_feature3') }}
            </li>
          </ul>
          <RouterLink
            to="/my/login"
            class="inline-flex items-center gap-2 mt-8 bg-sky-600 hover:bg-sky-500 text-white font-semibold rounded-lg px-6 py-3 transition-colors"
          >
            {{ t('participants_lp.hero.cta_secondary') }}
            <ArrowIcon direction="right" class="w-4 h-4" />
          </RouterLink>
        </div>
      </div>

      <!-- Spotlight 2: Participant dashboard (text left, image right) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div class="order-2 md:order-1">
          <p class="text-sky-600 font-semibold text-sm uppercase tracking-widest">
            {{ t('participants_lp.spotlights.dashboard_label') }}
          </p>
          <h2 class="text-3xl md:text-4xl font-bold text-slate-900 mt-3">
            {{ t('participants_lp.spotlights.dashboard_heading') }}
          </h2>
          <p class="text-slate-500 mt-4 leading-relaxed">
            {{ t('participants_lp.spotlights.dashboard_body') }}
          </p>
          <ul class="mt-6 space-y-2">
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('participants_lp.spotlights.dashboard_feature1') }}
            </li>
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('participants_lp.spotlights.dashboard_feature2') }}
            </li>
            <li class="flex items-center gap-3 text-slate-700">
              <span class="flex-shrink-0 w-5 h-5 bg-sky-100 text-sky-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
              {{ t('participants_lp.spotlights.dashboard_feature3') }}
            </li>
          </ul>
        </div>
        <div class="order-1 md:order-2 rounded-xl shadow-2xl aspect-video flex items-center justify-center">
          <div class="rounded-xl overflow-hidden shadow-2xl max-w-2xl mx-auto">
            <img :src="participantDashboardImage" alt="Participant Dashboard" class="border border-slate-200 shadow-xl w-full" />
          </div>
        </div>
      </div>

    </div>
  </section>
</template>
